import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AssetSelectionComponent } from '@features/shopping/asset/selection/asset-selection.component';
import { DeviceAsset, LineAsset, UserAsset } from '@models/dashboard.model';
import {
  MULTI_SIM_TYPES,
  constructAdditionalAssetTitle,
  constructAssetTitle,
  getCarrier
} from '@models/functions.model';
import { ShoppingCartData } from '@models/shopping-cart.model';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { ActivityManagerService } from '@services/activity/manager/activity-manager.service';
import { ActivityStateService } from '@services/activity/state/activity-state.service';
import { ModalService } from '@services/modal/modal.service';
import { ShoppingService } from '@services/shopping/shopping.service';
import { TranslationService } from '@services/translation/translation.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'ts-activity-10-line-selection',
  templateUrl: './line-selection.component.html',
  imports: [CommonModule, TranslationPipe, AssetSelectionComponent]
})
export class LineSelectionComponent implements OnInit, OnDestroy {
  @ViewChild('multiSimConfirmation') multi_sim_confirmation_template: TemplateRef<any>;
  asset: UserAsset;
  multi_sim_types: string[] = MULTI_SIM_TYPES;
  selected_line: LineAsset | undefined;
  shopping_cart: ShoppingCartData;
  private subscriptions: Subscription = new Subscription();

  constructor(
    private activity_manager_svc: ActivityManagerService,
    private activity_state_svc: ActivityStateService,
    private modal_svc: ModalService,
    private shopping_svc: ShoppingService,
    private translation_svc: TranslationService
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.shopping_svc.getShoppingItems().subscribe({
        next: (res: ShoppingCartData) => {
          this.shopping_cart = res;
        },
        error: (err: HttpErrorResponse) => {}
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  calculateModalAdditionalLines(): LineAsset[] {
    return this.asset.additional_lines?.filter((line: LineAsset) =>
      ['Active', 'Suspended'].includes(line.status)
    )!;
  }

  constructAdditionalAssetTitle(asset: DeviceAsset | LineAsset, type: number): string {
    return constructAdditionalAssetTitle(asset, type);
  }

  constructAssetTitle(include_details: boolean = false): string {
    return constructAssetTitle(this.asset, include_details);
  }

  lineInCart(line: LineAsset): boolean {
    return (
      line.id == this.shopping_cart.existing_asset?.line?.id &&
      this.asset?.device?.id == this.shopping_cart.existing_asset?.device?.id
    );
  }

  multisimModalBody(asset: UserAsset): string {
    return asset.multi_sim_type === this.multi_sim_types[0]
      ? this.translation_svc.translate('multisim_device_is', this.constructAssetTitle(true))
      : this.translation_svc.translate('multisim_line_number_is', asset.line.international_number);
  }

  multisimModalPrompt(asset: UserAsset): string {
    return asset.multi_sim_type === this.multi_sim_types[0]
      ? this.translation_svc.translate('multisim_select_eligible_line')
      : this.translation_svc.translate('multisim_devices_affected');
  }

  selectAsset(
    asset: UserAsset | LineAsset,
    from_modal: boolean = false,
    is_additional_asset: boolean = false
  ): void {
    if (!is_additional_asset) {
      this.asset = { ...(asset as UserAsset), asset_selection_case: 1 };
      this.selected_line = undefined;
    } else this.selected_line = asset as LineAsset;

    this.modal_svc.dismissAll();

    if (
      [this.multi_sim_types[0], this.multi_sim_types[1], this.multi_sim_types[6]].includes(
        (asset as UserAsset).multi_sim_type
      ) &&
      !from_modal
    )
      this.modal_svc.open(this.multi_sim_confirmation_template, {
        backdrop: 'static',
        centered: true
      });
    else
      this.subscriptions.add(
        this.shopping_svc.sendShoppingItems(this.shoppingCartParam(), true).subscribe({
          next: (res: ShoppingCartData) => {
            this.activity_manager_svc.navigateToNextStep();
          },
          error: (err: HttpErrorResponse) => {}
        })
      );
  }

  private shoppingCartParam(): ShoppingCartData {
    const { additional_devices, additional_lines, ...asset_without_additionals }: UserAsset =
      this.asset;
    return {
      ...this.shopping_cart,
      country_id: this.selected_line?.country_id,
      carrier: this.selected_line && getCarrier(this.selected_line),
      orderTypeId: this.activity_manager_svc.currentOrderTypeId(),
      lineId: this.selected_line?.id ?? this.asset?.line?.id,
      existing_asset: {
        ...asset_without_additionals,
        line: this.selected_line ?? this.asset?.line,
        asset_from_inventory: true
      },
      lost_stolen: this.activity_state_svc.parameter('lost_stolen'),
      progress_stepper: this.activity_manager_svc
        .state()
        .currentStep(this.activity_manager_svc.state().activeStepIndex())
    };
  }
}
